import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Column, Row } from 'further-ui/components/Layout';
import ActionButtons from 'further-ui/components/ActionButtons';
import BasicSelect from 'further-ui/components/BasicSelect';
import { CalendarDay } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import {
  AccountingReportDateOption,
  TrialBalanceGroupByOption,
} from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import FullHeightView from 'components/new/FullHeightView';
import PageFilters, { Filter } from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import getColumns from './getColumns';
import createTrialBalanceRow from './createTrialBalanceRow';
import TrialBalanceTotal from './TrialBalanceTotal';
import AccountingTable from '../components/AccountingTable';
import DatePresetFilter, {
  accountingDateLabels,
} from '../components/DatePresetFilter';

const pageId = 'trial-balance';

const groupByOptions = [
  { label: 'GL account', value: TrialBalanceGroupByOption.GlAccount },
  {
    label: 'Transaction type',
    value: TrialBalanceGroupByOption.TransactionType,
  },
];

const Container = styled(Column)(({ theme }) => ({
  paddingBottom: theme.spacing(theme.spacingSizes.xl),
}));

const TrialBalance: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: AccountingReportDateOption;
    customDate?: CalendarDay;
    groupBy: TrialBalanceGroupByOption;
  }>(pageId, {
    defaultFilters: {
      dateOption: AccountingReportDateOption.EndLastQuarter,
      customDate: undefined,
      groupBy: TrialBalanceGroupByOption.GlAccount,
    },
  });
  const { dateOption, customDate, groupBy } = filters;
  const isCustomDate = dateOption === AccountingReportDateOption.Custom;
  const isCustomDateUnset = isCustomDate && !customDate;
  const isGroupByGlAccount = groupBy === TrialBalanceGroupByOption.GlAccount;

  const {
    data: trialBalance,
    isLoading,
    isFetching,
  } = api.generalLedger.trialBalance.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
      groupBy,
    },
    {
      enabled: showOtherAccounting && !!selectedVehicleId && !isCustomDateUnset,
    },
  );

  const exportTrialBalance = api.generalLedger.exportTrialBalance.useMutation(
    useHandleTrpcMutation<{ file: string; fileName: string }>()({
      onSuccess: ({ file, fileName }) => {
        downloadXlxs(file, fileName);
      },
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (isCustomDateUnset) return;
    exportTrialBalance.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
      groupBy,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;
  const rows = trialBalance?.rows ?? [];
  const rowsWithId = rows.map((row, index) => {
    const idParts = [
      row.code,
      row.transactionType,
      row.accountName,
      row.accountType,
    ].filter(
      (part): part is string => typeof part === 'string' && part.length > 0,
    );
    return {
      ...row,
      id: idParts.length > 0 ? idParts.join('|') : `trial-balance-${index}`,
    };
  });
  const today = new CalendarDay(new Date());
  const periodEndDate = trialBalance?.periodEndDate?.isAfter(today)
    ? today
    : trialBalance?.periodEndDate;
  const columns = getColumns(
    baseCurrency,
    isGroupByGlAccount,
    trialBalance?.yearStartDate,
    periodEndDate,
  );
  const totalRow = createTrialBalanceRow(rows);

  return (
    <SyncScrollProvider>
      <FullHeightView
        actionBar={
          <TrialBalanceTotal
            isLoading={isFetching}
            baseCurrency={baseCurrency}
            isGroupByGlAccount={isGroupByGlAccount}
            totalDebitsYtd={totalRow.debitsYtd}
            totalCreditsYtd={totalRow.creditsYtd}
          />
        }
      >
        <Container spacing="lg" fullHeight>
          <Row
            spacing="sm"
            justify="space-between"
            alignItems="flex-end"
            breakpoint="sm"
          >
            <Column spacing="xs">
              <Typography variant="h5" color="grey.900">
                Trial balance
              </Typography>
              <Typography variant="body2">
                {vehicle.vehicleName} as at{' '}
                {isCustomDate
                  ? isCustomDateUnset
                    ? '...'
                    : customDate?.toReadable()
                  : accountingDateLabels[dateOption].toLowerCase()}
                {isCustomDate ? (isCustomDateUnset ? '' : '.') : '.'}
              </Typography>
            </Column>
            <ActionButtons
              actions={[
                {
                  label: 'Export',
                  variant: 'contained',
                  onClick: handleExport,
                  disabled: isCustomDateUnset,
                },
              ]}
            />
          </Row>
          <Row spacing="sm" justify="space-between" breakpoint="md">
            <PageFilters>
              <DatePresetFilter
                dateOption={dateOption}
                customDate={customDate}
                onChange={(dateOption, customDate) =>
                  handleChangeFilters({ dateOption, customDate })
                }
              />
            </PageFilters>
            <PageFilters justify="flex-end">
              <Filter size="sm">
                <BasicSelect
                  fullWidth
                  options={groupByOptions}
                  value={filters.groupBy}
                  onChange={(groupBy) => handleChangeFilters({ groupBy })}
                  placeholder="Select group"
                />
              </Filter>
            </PageFilters>
          </Row>
          <AccountingTable
            columns={columns}
            rows={rowsWithId}
            emptyMessage="No accounts to display on the trial balance."
            isLoading={isFetching}
            bodyRowPaddingY={rowsWithId.length === 0 ? 30 : undefined}
          />
        </Container>
      </FullHeightView>
    </SyncScrollProvider>
  );
};

export default TrialBalance;
